// Design tokens for generated PDFs. Palette, type-scale, geometry, and
// status tones mirror the backend Puppeteer templates so FE and BE PDFs
// read as one family. Keep this file as the single source of truth —
// documents should never hard-code colours or sizes.
//
// Palette philosophy: slate neutrals do the heavy lifting; brand blue is
// used sparingly as an accent on the hero number, not as a page-wide tint.
// This is the "serious school prospectus" aesthetic, not a landing page.

export const pdfColors = {
  // Brand accents — use sparingly, on roll numbers / section accents only.
  brand: '#1e3a8a',       // slate-blue-900 — logo chip border, hero accents
  brandAccent: '#3b82f6', // blue-500 — pill ink, hyperlink-equivalent
  brandSoft: '#dbeafe',   // blue-100 — subtle brand background wash

  // Text levels — slate scale for serious document feel.
  textPrimary: '#0f172a',   // slate-900
  textSecondary: '#475569', // slate-600
  textMuted: '#64748b',     // slate-500
  textFaint: '#94a3b8',     // slate-400 — hints, placeholder
  textOnDark: '#ffffff',

  // Surfaces + separators.
  surface: '#ffffff',
  surfaceSoft: '#f8fafc',    // slate-50 — card bg, zebra rows, logo chip
  surfaceSofter: '#f9fafb',  // gray-50 — alternate zebra
  divider: '#e2e8f0',        // slate-200
  dividerStrong: '#cbd5e1',  // slate-300
  rule: '#f1f5f9',           // slate-100 — within-group hairlines

  // Status (bg + fg + border) — matched to backend fee-invoice tones.
  // Access via pdfStatusTones[tone] below rather than reaching for raw.
  success: '#14532d',
  successSoft: '#dcfce7',
  successBorder: '#86efac',

  warning: '#78350f',
  warningSoft: '#fef3c7',
  warningBorder: '#fcd34d',

  danger: '#7f1d1d',
  dangerSoft: '#fee2e2',
  dangerBorder: '#fca5a5',

  info: '#075985',
  infoSoft: '#e0f2fe',
  infoBorder: '#7dd3fc',

  neutralText: '#475569',
  neutralSoft: '#f1f5f9',
  neutralBorder: '#cbd5e1',
} as const;

/** Status pill tones — one source of truth, keyed by semantic name. */
export const pdfStatusTones = {
  primary: { fg: pdfColors.brand,        bg: pdfColors.brandSoft,     border: '#93c5fd' },
  success: { fg: pdfColors.success,      bg: pdfColors.successSoft,   border: pdfColors.successBorder },
  warning: { fg: pdfColors.warning,      bg: pdfColors.warningSoft,   border: pdfColors.warningBorder },
  danger:  { fg: pdfColors.danger,       bg: pdfColors.dangerSoft,    border: pdfColors.dangerBorder },
  info:    { fg: pdfColors.info,         bg: pdfColors.infoSoft,      border: pdfColors.infoBorder },
  neutral: { fg: pdfColors.neutralText,  bg: pdfColors.neutralSoft,   border: pdfColors.neutralBorder },
} as const;

export type PdfTone = keyof typeof pdfStatusTones;

export const pdfSpacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 18,
  xl: 24,
  xxl: 36,
  pageX: 40,
} as const;

/**
 * Page frame geometry. Every portal PDF is laid out inside this frame, so
 * body content can never reach the footer or the running header:
 *
 *   ┌──────────────────────────────────────┐
 *   │ runningHeaderTop                      │  pages 2+: slim running header
 *   │ ───────────────────────────────────── │
 *   │ contentTop  (page 1: masthead pulls   │
 *   │             up into this band)        │
 *   │   … body flows here …                 │
 *   │ contentBottom (reserved, never used)  │
 *   │ ───────────────────────────────────── │
 *   │ footerBottom                          │  every page: footer
 *   └──────────────────────────────────────┘
 *
 * `contentBottom` = footerBottom + footerHeight + breathing room. The footer
 * is a fixed-height pair of lines, so the reservation is exact.
 */
export const pdfPageFrame = {
  runningHeaderTop: 22,
  contentTop: 52,
  mastheadTop: 30,
  footerBottom: 14,
  footerHeight: 28,
  contentBottom: 58,
} as const;

// Point sizes. @react-pdf's unit is 72 pt/inch; keep these in pt so they
// translate 1:1 to the backend scale (where sizes are stated in pt).
export const pdfFontSize = {
  xxs: 6.8,
  xs: 7.5,
  sm: 9,
  md: 10,
  lg: 11,
  xl: 13,
  xxl: 16,
  display: 22,
  hero: 28,
} as const;

export const pdfRadius = {
  sm: 3,
  md: 5,
  lg: 8,
  pill: 999,
} as const;

export const pdfFontFamily = {
  // Vendored under public/fonts/pdf and registered in fonts.ts — the same
  // families the backend Puppeteer templates use. Never a CDN URL.
  sans: 'Inter',
  mono: 'JetBrains Mono',
  // The classic letter serif. Built into every PDF reader (one of the 14
  // standard fonts), so nothing is fetched or embedded. Bold and italic are
  // their own families.
  serif: 'Times-Roman',
  serifBold: 'Times-Bold',
  serifItalic: 'Times-Italic',
} as const;

/** Letter-spacing presets (in pt). Tight for display text, open for
 *  uppercase micro-labels (the backend's signature micro-detail). */
export const pdfLetter = {
  tight: -0.2,
  normal: 0,
  openLabel: 0.6,
  openStrong: 1.2,
} as const;
